import type { components } from '../../api-types';
import { API_BASE_URL, apiClient } from './index';

export type OrderForm = components['schemas']['OrderFormDto'];
export type OrderFormSummary = components['schemas']['OrderFormSummaryDto'];
export type SaveOrderFormInput = components['schemas']['SaveOrderFormDto'];
export type OrderFormInput = Omit<SaveOrderFormInput, 'quoteName'>;
export type PreviewOrderFormInput =
  components['schemas']['PreviewOrderFormDto'];
export type OrderFormPreview = components['schemas']['OrderFormPreviewDto'];
export type OrderFormStatus = components['schemas']['OrderFormStatus'];
export type OrderFormContent = components['schemas']['OrderFormContentDto'];
export type Countersignature = components['schemas']['CountersignDto'];
export type OrderFormTemplate = components['schemas']['OrderFormTemplateDto'];
export type ApprovalQueueItem = components['schemas']['ApprovalQueueItemDto'];
export type RenewalTerms = components['schemas']['RenewalTermsDto'];
export type SalesReport = components['schemas']['SalesReportDto'];
export type CreateTemplateInput =
  components['schemas']['CreateOrderFormTemplateVersionDto'];
export type AttachedTerms = components['schemas']['AttachedTermsDto'];

export const uploadTermsDocument = async (file: File) => {
  const body = new FormData();
  body.append('file', file);
  const { data } = await apiClient.post<AttachedTerms>(
    '/order-forms/terms-documents',
    body,
  );
  return data;
};

export const getOrderFormTermsUrl = (id: string) =>
  `${API_BASE_URL}/order-forms/${id}/terms`;

export const getOrderForms = async () => {
  const { data } = await apiClient.get<OrderFormSummary[]>('/order-forms');
  return data;
};

export const getOrderForm = async (id: string) => {
  const { data } = await apiClient.get<OrderForm>(`/order-forms/${id}`);
  return data;
};

export const previewOrderForm = async (input: PreviewOrderFormInput) => {
  const { data } = await apiClient.post<OrderFormPreview>(
    '/order-forms/preview',
    input,
  );
  return data;
};

export const createOrderForm = async (input: SaveOrderFormInput) => {
  const { data } = await apiClient.post<OrderForm>('/order-forms', input);
  return data;
};

export const updateOrderForm = async (
  id: string,
  input: SaveOrderFormInput,
) => {
  const { data } = await apiClient.put<OrderForm>(`/order-forms/${id}`, input);
  return data;
};

export const getApprovalQueue = async () => {
  const { data } = await apiClient.get<ApprovalQueueItem[]>(
    '/order-form-approvals',
  );
  return data;
};

const postAction = async <T>(id: string, action: string, body: object = {}) => {
  const { data } = await apiClient.post<T>(
    `/order-forms/${id}/${action}`,
    body,
  );
  return data;
};

export const submitOrderForm = (id: string) =>
  postAction<OrderForm>(id, 'submit');
export const approveOrderForm = (id: string) =>
  postAction<OrderForm>(id, 'approve');
export const returnOrderForm = (id: string, reason: string) =>
  postAction<OrderForm>(id, 'return', { reason });
export const sendOrderForm = (id: string) =>
  postAction<{ reviewUrl: string }>(id, 'send');
export const commentOnOrderForm = (id: string, body: string) =>
  postAction<OrderForm>(id, 'comments', { body });
export const voidOrderForm = (id: string) => postAction<OrderForm>(id, 'void');
export const reassignOrderForm = (id: string, ownerUserId: string) =>
  postAction<OrderForm>(id, 'reassign', { ownerUserId });
export const countersignOrderForm = (id: string, input: Countersignature) =>
  postAction<OrderForm>(id, 'countersign', input);
export const completeAdminSetup = (id: string) =>
  postAction<OrderForm>(id, 'complete-admin-setup');

export const updateBillingSettings = async (
  id: string,
  input: { enabled: boolean; reason?: string },
) => {
  const { data } = await apiClient.post<OrderForm>(
    `/order-forms/${id}/billing`,
    input,
  );
  return data;
};

export const cancelSubscription = (id: string) =>
  postAction<OrderForm>(id, 'subscription/cancel');

export const sendBillingReminder = (id: string) =>
  postAction<OrderForm>(id, 'billing/reminder');

export const getOrderFormPdfUrl = (id: string) =>
  `${API_BASE_URL}/order-forms/${id}/pdf`;

export const getSalesReport = async () => {
  const { data } = await apiClient.get<SalesReport>('/reports/sales');
  return data;
};

export const getTemplateVersions = async () => {
  const { data } = await apiClient.get<OrderFormTemplate[]>(
    '/order-form-template/versions',
  );
  return data;
};

export const createTemplateVersion = async (input: CreateTemplateInput) => {
  const { data } = await apiClient.post<OrderFormTemplate>(
    '/order-form-template/versions',
    input,
  );
  return data;
};
